import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

// Callable reference to the Firebase Cloud Function (functions/aiMentor.js).
// This replaces the old raw fetch("/.netlify/functions/ai-mentor") call —
// Firebase handles the network request and automatically attaches the
// signed-in user's auth token, which the function checks on the other end.
const aiMentor = httpsCallable(functions, "aiMentor");

const MAX_RETRIES = 3;
const FALLBACK_WAIT = 45000; // ms

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

// Fakes a "typing" effect by feeding the already-complete response to the
// caller a few characters at a time. Gemini doesn't actually stream through
// a callable function, so this just makes the UI feel responsive.
const simulateStream = async (text, onChunk) => {
    if (!onChunk) return;
    const CHUNK_SIZE = 6;
    const DELAY_MS = 18;
    for (let i = 0; i < text.length; i += CHUNK_SIZE) {
        onChunk(text.slice(i, i + CHUNK_SIZE));
        await sleep(DELAY_MS);
    }
};

export const getGeminiStreamResponse = async (userQuery, retrievedContext = "", onChunk) => {
    let attempt = 0;

    while (attempt < MAX_RETRIES) {
        try {
            const result = await aiMentor({ action: "generate", userQuery, retrievedContext });
            const fullResponse = result.data?.text || "";
            await simulateStream(fullResponse, onChunk);
            return fullResponse;
        } catch (error) {
            console.error("CRITICAL: Gemini Service Failure", error);

            // Callable functions surface errors as error.code strings
            // (e.g. "functions/resource-exhausted") instead of HTTP status
            // codes — these map to the HttpsError codes thrown in aiMentor.js.
            if (error.code === "functions/resource-exhausted") {
                attempt++;
                if (attempt < MAX_RETRIES) {
                    if (onChunk) onChunk(`\n\n⏳ *API rate limit hit — retrying…*\n\n`);
                    await sleep(FALLBACK_WAIT);
                    continue;
                }
                return `⚠️ **API quota exceeded.** ${error.message || "Please wait a moment and try again."}`;
            }

            if (error.code === "functions/unauthenticated") {
                return "❌ You must be signed in to use the AI Mentor.";
            }

            return error.message
                ? `❌ ${error.message}`
                : "❌ The Mentor is currently offline. Please try again shortly.";
        }
    }
};
